import { ApolloClient } from '@apollo/client';
import { Linking, Platform } from 'react-native';

import {
  GetRateUsStateDocument,
  GetRateUsStateQuery,
  RateUsTriggerInput,
  UpdateRateUsStateAttributes,
  UpdateRateUsStateDocument,
  UpdateRateUsStateMutation,
  UpdateRateUsStateMutationVariables,
} from '@graphql/generated';
import { AsyncStorage } from '@utils/storage';

// Deep links straight to the "write a review" sheet on each store
export const APP_STORE_REVIEW_URL =
  'https://apps.apple.com/app/id6448815209?action=write-review';
export const PLAY_STORE_REVIEW_URL =
  'https://play.google.com/store/apps/details?id=com.eloveit.TaskTag';

// Boolean flag written before the state moved to the server. We keep writing
// it so clients still running the old MyProfile code (pre-OTA) keep hiding
// the rate button, and we read it once to migrate users who already rated.
export const LEGACY_APP_RATED_STORAGE_KEY = 'app_rated';

/** Any Apollo client capable of running the Rate Us query/mutation */
export type RateAppClient = Pick<ApolloClient<object>, 'query' | 'mutate'>;

export type RateAppTriggerKey =
  | 'upload_media'
  | 'checkin'
  | 'checklist'
  | 'tasks'
  | 'chat_tags'
  | 'assign_tasks';

export type RateAppTriggerState = {
  count: number;
  prompt_stage: number;
  last_prompt_at: string | null;
};

export type RateAppPromptState = {
  has_rated: boolean;
  permanently_suppressed: boolean;
  triggers: Record<RateAppTriggerKey, RateAppTriggerState>;
};

const TRIGGER_KEYS: RateAppTriggerKey[] = [
  'upload_media',
  'checkin',
  'checklist',
  'tasks',
  'chat_tags',
  'assign_tasks',
];

/** Maps local snake_case trigger keys to the server's camelCase fields */
const SERVER_FIELD_BY_TRIGGER = {
  upload_media: 'uploadMedia',
  checkin: 'checkin',
  checklist: 'checklist',
  tasks: 'tasks',
  chat_tags: 'chatTags',
  assign_tasks: 'assignTasks',
} as const;

/**
 * Escalating prompt schedule, indexed by the trigger's `prompt_stage`.
 * Each stage fires when the cumulative event count reaches `count`, or —
 * from the second stage on — when `days` have passed since the previous
 * prompt, whichever comes first. Once every stage has been shown and
 * dismissed, the prompt is permanently suppressed.
 */
export const PROMPT_STAGES: ReadonlyArray<{
  count: number;
  days: number | null;
}> = [
  { count: 3, days: null },
  { count: 10, days: 3 },
  { count: 20, days: 7 },
];

const DAY_MS = 24 * 60 * 60 * 1000;

// All Rate Us updates are read-modify-write against an absolute server
// state. Two concurrent calls would read the same cached count and the
// later write would silently drop the earlier increment, so every op runs
// through this queue.
let opQueue: Promise<unknown> = Promise.resolve();

const serialize = <T>(op: () => Promise<T>): Promise<T> => {
  const run = opQueue.then(op, op);
  opQueue = run.then(
    () => undefined,
    () => undefined
  );
  return run;
};

const createDefaultTriggerState = (): RateAppTriggerState => ({
  count: 0,
  prompt_stage: 0,
  last_prompt_at: null,
});

export const createDefaultRateAppPromptState = (): RateAppPromptState => ({
  has_rated: false,
  permanently_suppressed: false,
  triggers: TRIGGER_KEYS.reduce(
    (acc, key) => ({ ...acc, [key]: createDefaultTriggerState() }),
    {} as Record<RateAppTriggerKey, RateAppTriggerState>
  ),
});

/**
 * Pure decision: should the prompt be shown for this trigger right now?
 * Exported separately from the storage flow so it can be unit-tested.
 */
export const shouldPromptForTrigger = (
  state: RateAppPromptState,
  trigger: RateAppTriggerKey,
  nowMs: number
): boolean => {
  if (state.has_rated || state.permanently_suppressed) return false;

  const triggerState = state.triggers[trigger];
  if (!triggerState) return false;

  const stage = PROMPT_STAGES[triggerState.prompt_stage];
  if (!stage) return false;

  if (triggerState.count >= stage.count) return true;

  if (stage.days !== null && triggerState.last_prompt_at) {
    const lastPromptMs = Date.parse(triggerState.last_prompt_at);
    if (!Number.isNaN(lastPromptMs)) {
      return nowMs - lastPromptMs >= stage.days * DAY_MS;
    }
  }
  return false;
};

/** Maps the server's camelCase RateUsState onto the local shape */
const mapServerState = (
  server: GetRateUsStateQuery['getRateUsState']
): RateAppPromptState => {
  const state = createDefaultRateAppPromptState();
  state.has_rated = server.hasRated;
  state.permanently_suppressed = server.permanentlySuppressed;
  for (const key of TRIGGER_KEYS) {
    const serverTrigger = server.triggers[SERVER_FIELD_BY_TRIGGER[key]];
    if (!serverTrigger) continue;
    state.triggers[key] = {
      count: serverTrigger.count,
      prompt_stage: serverTrigger.promptStage,
      last_prompt_at: serverTrigger.lastPromptAt ?? null,
    };
  }
  return state;
};

/**
 * Pushes a partial update to the server and syncs the returned full state
 * into the Apollo cache so `useGetRateUsStateQuery` consumers stay fresh
 * (RateUsState has no id, so the cache can't merge it automatically).
 */
const pushRateUsUpdate = (
  client: RateAppClient,
  attributes: UpdateRateUsStateAttributes
) =>
  client.mutate<UpdateRateUsStateMutation, UpdateRateUsStateMutationVariables>({
    mutation: UpdateRateUsStateDocument,
    variables: { attributes },
    update: (cache, { data }) => {
      if (!data?.updateRateUsState) return;
      cache.writeQuery({
        query: GetRateUsStateDocument,
        data: { getRateUsState: data.updateRateUsState },
      });
    },
  });

/**
 * Loads the server-side Rate Us state for the current user. Users who rated
 * on an older build only have the local legacy flag — migrate it to the
 * server the first time we see it.
 */
export const loadRateAppPromptState = async (
  client: RateAppClient
): Promise<RateAppPromptState> => {
  const { data } = await client.query<GetRateUsStateQuery>({
    query: GetRateUsStateDocument,
    // First use hits the network; afterwards pushRateUsUpdate keeps the
    // cached getRateUsState in sync, so repeated triggers read from cache
    fetchPolicy: 'cache-first',
  });
  const state = mapServerState(data.getRateUsState);

  if (!state.has_rated) {
    let legacy: string | null = null;
    try {
      legacy = await AsyncStorage.getItem(LEGACY_APP_RATED_STORAGE_KEY);
    } catch {
      // Unreadable legacy flag — treat as not rated
    }
    if (legacy === 'true') {
      state.has_rated = true;
      await pushRateUsUpdate(client, { hasRated: true });
    }
  }
  return state;
};

export type RateAppTriggerResult = {
  shouldPrompt: boolean;
  /**
   * 1-based ordinal of the prompt being shown for this trigger (1 = first
   * prompt, 2 = second, ...). 0 when `shouldPrompt` is false.
   */
  stage: number;
  /** Cumulative event count for this trigger after the increment */
  count: number;
  /** The trigger's current prompt_stage — for analytics */
  promptStage: number;
};

/**
 * Records one or more occurrences of a trigger event (`times` > 1 covers
 * batch actions like assigning several people at once) and returns whether
 * the rate prompt should be shown now. Recording never advances the
 * trigger's stage — the caller must invoke `markRateAppPromptShown` when the
 * prompt actually reaches the screen, so a prompt that gets dropped (app
 * killed, celebration dismissed, another prompt already queued) is not
 * silently consumed.
 */
export const recordRateAppTrigger = (
  client: RateAppClient,
  trigger: RateAppTriggerKey,
  nowMs: number = Date.now(),
  times = 1
): Promise<RateAppTriggerResult> =>
  serialize(async () => {
    const state = await loadRateAppPromptState(client);
    const triggerState = state.triggers[trigger];
    triggerState.count += Math.max(1, Math.floor(times));

    const shouldPrompt = shouldPromptForTrigger(state, trigger, nowMs);

    const triggerInput: RateUsTriggerInput = {
      count: triggerState.count,
      promptStage: triggerState.prompt_stage,
      // Omitted when never prompted — the server keeps it unchanged
      ...(triggerState.last_prompt_at
        ? { lastPromptAt: triggerState.last_prompt_at }
        : {}),
    };
    await pushRateUsUpdate(client, {
      triggers: { [SERVER_FIELD_BY_TRIGGER[trigger]]: triggerInput },
    });

    return {
      shouldPrompt,
      stage: shouldPrompt ? triggerState.prompt_stage + 1 : 0,
      count: triggerState.count,
      promptStage: triggerState.prompt_stage,
    };
  });

/**
 * Advances the trigger to its next stage. Call this when the rate prompt is
 * actually displayed — not when it merely becomes due — so prompts that
 * never reach the screen can fire again on the next trigger event.
 *
 * Does NOT stamp `last_prompt_at` — the days-based re-prompt window is
 * anchored to when the prompt was *dismissed*, not when it was shown (see
 * `markRateAppPromptDismissed`). Stamping it here would let a modal left
 * open for a long time before "Later" make the next stage eligible too
 * early.
 */
export const markRateAppPromptShown = (
  client: RateAppClient,
  trigger: RateAppTriggerKey
): Promise<void> =>
  serialize(async () => {
    const state = await loadRateAppPromptState(client);
    const triggerState = state.triggers[trigger];
    triggerState.prompt_stage += 1;
    await pushRateUsUpdate(client, {
      triggers: {
        [SERVER_FIELD_BY_TRIGGER[trigger]]: {
          count: triggerState.count,
          promptStage: triggerState.prompt_stage,
        },
      },
    });
  });

/**
 * Stamps `last_prompt_at` at the moment the prompt is dismissed (Yes or
 * Later). The 3-day/7-day re-prompt windows in `PROMPT_STAGES` are measured
 * from this timestamp, per the TA-9183 QA finding — using the shown time
 * instead let later stages become eligible too early when a modal sat open.
 */
export const markRateAppPromptDismissed = (
  client: RateAppClient,
  trigger: RateAppTriggerKey,
  nowMs: number = Date.now()
): Promise<void> =>
  serialize(async () => {
    const state = await loadRateAppPromptState(client);
    const triggerState = state.triggers[trigger];
    triggerState.last_prompt_at = new Date(nowMs).toISOString();
    await pushRateUsUpdate(client, {
      triggers: {
        [SERVER_FIELD_BY_TRIGGER[trigger]]: {
          count: triggerState.count,
          promptStage: triggerState.prompt_stage,
          lastPromptAt: triggerState.last_prompt_at,
        },
      },
    });
  });

/**
 * Permanently suppresses the prompt across all triggers. Called when the
 * user dismisses the final (3rd) prompt with "Later" — not when that prompt
 * is merely shown, so a killed app doesn't silently opt the user out.
 */
export const suppressRateAppPrompt = (client: RateAppClient): Promise<void> =>
  serialize(async () => {
    await pushRateUsUpdate(client, { permanentlySuppressed: true });
  });

/**
 * Marks the app as rated on the server and mirrors the legacy local flag
 * for clients still running the pre-server MyProfile code.
 */
export const markAppRated = async (client: RateAppClient): Promise<void> => {
  await pushRateUsUpdate(client, { hasRated: true });
  await AsyncStorage.setItem(LEGACY_APP_RATED_STORAGE_KEY, 'true');
};

/**
 * Opens the platform store review page. Stores don't report whether a
 * review was actually submitted, so callers treat "opened the review page"
 * as rated.
 */
export const openStoreReviewPage = (): Promise<unknown> =>
  Linking.openURL(
    Platform.OS === 'ios' ? APP_STORE_REVIEW_URL : PLAY_STORE_REVIEW_URL
  );
